import { getSnapshot, types } from 'mobx-state-tree'
import {
  editableModel,
  Form,
  IPropertyOwner,
  IQuestion,
  IQuestionInfo,
  PropertyOwnerModel,
  Question,
  routes2,
} from '@linqpal/models'
import { sections } from './sections'
import {
  Categories,
  CATEGORIES,
  ProjectSections,
  PROJECT_SECTIONS,
  PROJECT_TYPE,
  PROPERTY_OWNER_TYPE,
  ROLES,
} from '@linqpal/models/src/dictionaries'
import { ICreateProjectDTO } from '@linqpal/models/src/routes2/project/types'
import { UUID } from '../../utils/helpers/commonUtils'
import RootStore from '../../store'
import _ from 'lodash'
import InvoiceActionsStore from '../Contractor/InvoicesTab/InvoiceActionsStore'

const props = {
  formData: types.maybeNull(Form),
  s3Folder: types.optional(types.string, ''),
}

const views = (self) => ({
  get currentSection() {
    return sections[self.formData?.currentSection]
  },
  get questionsUnderCurrentSection(): IQuestionInfo[] {
    const qIds = self.formData?.data
      ?.get(self.formData?.currentSection)
      ?.items.map((i) => i.identifier)
    const r = qIds?.map(
      (id) => sections[self.formData?.currentSection].allQuestions[id],
    )
    return r
  },
  get progress(): number {
    return (
      (Object.keys(sections).indexOf(self.formData?.currentSection) + 1) /
      Object.keys(sections).length
    )
  },
  get isCurrentSectionFilled(): boolean {
    const current = self.formData?.currentSection
    return self.formData?.data.get(current)?.items.every((i) => {
      const isOptional = self.isOptional(current, i.identifier)
      return isOptional || i.filled
    })
  },
  get isLastSection(): boolean {
    const s = Object.keys(sections)
    return s[s.length - 1] === self.formData?.currentSection
  },
  get isFirstSection(): boolean {
    const s = Object.keys(sections)
    return s[0] === self.formData?.currentSection
  },
  get allSectionsFilled() {
    const sectionIds = Object.keys(sections)
    return sectionIds.every((id) => self.formData?.data.get(id)?.filled)
  },
  get isSubcontractor() {
    return (
      self.getContent(PROJECT_SECTIONS.INFORMATION, 'role') ===
      ROLES.SUBCONTRACTOR
    )
  },
})

const actions = (self) => ({
  setS3Folder(val) {
    self.s3Folder = val
  },
  setFormData(val) {
    self.formData = val
  },
  isIndividualOwner(content) {
    return content?.type === PROPERTY_OWNER_TYPE.INDIVIDUAL
  },
  isOptional(sectionId, qId) {
    return sections[sectionId].allQuestions[qId].optional
  },
  isBusiness(content) {
    return content?.type === PROPERTY_OWNER_TYPE.BUSINESS
  },
  isContractValueMoreThan(val = 100000) {
    const contractValue = self.formData?.data
      ?.get(PROJECT_SECTIONS.INFORMATION)
      ?.items?.find((i) => i.identifier === 'contractValue')?.content
    return Number(contractValue) > val
  },
  getContent(section, question) {
    return self.formData?.data
      ?.get(section)
      ?.items?.find((i) => i.identifier === question)?.content
  },
  model(sectionId, question) {
    const { identifier, shouldProcessAnswer, Model, title } = question
    const titleFromId = _.capitalize(identifier.split(/(?=[A-Z])/).join(' '))
    const titleToSet = title || titleFromId
    const setValue = (value) => {
      const currentValue = self.getContent(sectionId, identifier)
      self.formData?.setData(sectionId, identifier, value, !!value, titleToSet)
      if (shouldProcessAnswer && currentValue !== value)
        self.onAnswer(sectionId, identifier)
    }
    const setModelValue = (name, value) => {
      const currentValue = self.formData?.data
        ?.get(sectionId)
        ?.items?.find((i) => i.identifier === identifier).content
      const val = { ...currentValue, [name]: value }
      const filled = Model.create(val).filled
      self.formData?.setData(sectionId, identifier, val, filled, titleToSet)
      shouldProcessAnswer && self.onAnswer(sectionId, identifier)
    }
    const setMultipleValue = (name, value, id) => {
      const currentValue = self.formData?.data
        ?.get(sectionId)
        ?.items?.find((i) => i.identifier === identifier).content
      let val
      if (!currentValue) {
        val = [getSnapshot(Model.create({ [name]: value }))]
      } else {
        val = currentValue.map((cv) => {
          if (cv.id === id) return { ...cv, [name]: value }
          else return { ...cv }
        })
      }
      const filled = val.every((v) => Model.create(v).filled)
      self.formData?.setData(sectionId, identifier, val, filled, titleToSet)
    }
    return {
      setValue,
      setModelValue,
      setMultipleValue,
    }
  },
  getQuestionsList(section): IQuestion[] {
    const list = sections[section].initialQuestions?.map((i) => {
      const { identifier, title, Model, multipleAnswers } = i
      const content = Model
        ? multipleAnswers
          ? [getSnapshot(Model.create({ id: UUID() }))]
          : getSnapshot(Model.create({}))
        : ''
      return {
        identifier,
        title,
        content,
        filled: false,
      }
    })
    return list
  },
  getList(list: IQuestionInfo[], currentSection: string) {
    const res: (IQuestion | undefined)[] = list.map((i) => {
      const { identifier, title } = i
      const existing = self.formData?.data
        ?.get(currentSection)
        ?.items?.find((item) => item.identifier === identifier)
      return (
        existing ||
        Question.create({
          identifier,
          title,
          content: '',
          filled: false,
        })
      )
    })
    return res
  },
  setUpSection(section: string) {
    const list = self.getQuestionsList(section)
    self.formData?.setCurrent(section, list)
  },
  setUpNextSection() {
    const sectionsList = Object.keys(sections)
    const next =
      sectionsList[sectionsList.indexOf(self.formData?.currentSection) + 1]
    self.setUpSection(next)
  },
  setUpPreviousSection() {
    const sectionsList = Object.keys(sections)
    const next =
      sectionsList[sectionsList.indexOf(self.formData?.currentSection) - 1]
    self.setUpSection(next)
  },
  resetProjectType() {
    const Qlist = self.getQuestionsList(PROJECT_SECTIONS.TYPE)
    self.formData?.resetSectionQuestions(Qlist, PROJECT_SECTIONS.TYPE)
  },
  addQuestionsToInitialList(newList, section) {
    const initialIds = sections[section].initialQuestions.map(
      (i) => i.identifier,
    )
    const ini = self.formData?.data
      ?.get(section)
      ?.items.filter((q) => initialIds.includes(q.identifier))
    self.formData?.resetSectionQuestions([...ini, ...newList], section)
  },
  addQuestionsToCurrentList(newList, section) {
    const currentQuestions = self.formData?.data?.get(section)?.items
    self.formData?.resetSectionQuestions(
      [...currentQuestions, ...newList],
      section,
    )
  },
  removeQuestion(questionId, section) {
    const currentQuestions = self.formData?.data?.get(section)?.items
    const newSet = currentQuestions.filter((q) => q.identifier !== questionId)
    self.formData?.resetSectionQuestions(newSet, section)
  },
  onAnswer(currentSection: ProjectSections, currentQuestion: string) {
    const t = self.getContent(currentSection, currentQuestion)
    switch (currentSection) {
      case PROJECT_SECTIONS.ADDRESS: {
        let list = []
        const newSet = sections.propertyAddress.isNoticeRequired(t)
        const noticeUploaded = self.formData?.data
          ?.get(PROJECT_SECTIONS.ADDRESS)
          ?.items.find((i) => i.identifier === 'noticeOfCommencement')?.filled
        if (newSet) {
          if (noticeUploaded) return
          else list = self.getList(newSet, currentSection)
        }
        self.addQuestionsToInitialList(list, currentSection)
        return
      }
      case PROJECT_SECTIONS.INFORMATION: {
        let list = []
        if (currentQuestion === 'role') {
          const newSet =
            sections.projectInformation.isPrimeContractorDetailsRequired(t)
          if (newSet) {
            list = self.getList(newSet, currentSection)
          }
          self.addQuestionsToInitialList(list, currentSection)
          const type = self.getContent(PROJECT_SECTIONS.TYPE, 'type')
          if (type === PROJECT_TYPE.PUBLIC) self.resetProjectType() // Because public type checks if role is subcontractor
        } else if (currentQuestion === 'contractValue') {
          const type = self.getContent(PROJECT_SECTIONS.TYPE, 'type')
          if (type === PROJECT_TYPE.FEDERAL) self.resetProjectType()
        }
        return
      }
      case PROJECT_SECTIONS.TYPE: {
        if (currentQuestion === 'type') {
          const newSet =
            sections.projectType.isPrivate(t) ||
            sections.projectType.isPublic(t) ||
            sections.projectType.isFederal(t, self.isContractValueMoreThan())
          if (newSet) {
            const list = self.getList(newSet, currentSection)
            self.addQuestionsToInitialList(list, currentSection)
          }
        } else if (currentQuestion === 'bondExists') {
          const newSet = sections.projectType.isBondExists(t)
          if (newSet) {
            const list = self.getList(newSet, currentSection)
            self.addQuestionsToCurrentList(list, currentSection)
          } else {
            self.removeQuestion('uploadBond', currentSection)
          }
        }
        return
      }
      default:
        return
    }
  },
  sectionCategory(section: ProjectSections): Categories {
    const isCompleted = self.formData?.data
      .get(section)
      ?.items.every((i) => self.isOptional(section, i.identifier) || i.filled)
    if (self.formData?.currentSection === section) {
      return CATEGORIES.CURRENT
    } else if (isCompleted) {
      return CATEGORIES.FILLED
    } else {
      return CATEGORIES.PENDING
    }
  },
  addAnotherOwner(
    currentValue: Omit<IPropertyOwner, 'filled' | 'addressFilled'>[],
  ) {
    const newVal = [...currentValue]
    newVal.push(getSnapshot(PropertyOwnerModel.create({ id: UUID() })))
    self.formData?.setData(
      PROJECT_SECTIONS.PROPERTY_OWNER,
      'details',
      newVal,
      false,
    )
  },
  removeOwner(
    currentValue: Omit<IPropertyOwner, 'filled' | 'addressFilled'>[],
    idToRemove: string,
  ) {
    const filtered = currentValue.filter((item) => item.id !== idToRemove)
    const newFilled = filtered.every((v) => PropertyOwnerModel.create(v).filled)
    self.formData?.setData(
      PROJECT_SECTIONS.PROPERTY_OWNER,
      'details',
      filtered,
      newFilled,
    )
  },
  submit(navigation, reloadProjects, ids?: string, projectId?: string) {
    const data = getSnapshot(self.formData) as ICreateProjectDTO
    if (projectId) {
      routes2.project
        .update(projectId, data)
        .then(() => {
          console.log(projectId, 'Project updated')
          navigation.goBack()
          reloadProjects()
        })
        .catch((err) => console.log(err))
    } else {
      const {
        streetAddress,
        unitNum,
        state,
        city,
        zip,
        houseNum,
        subdivision,
        lotNum,
        blockNum,
      } = self.getContent(PROJECT_SECTIONS.ADDRESS, 'address')

      const address = [
        streetAddress,
        unitNum,
        state,
        city,
        zip,
        houseNum,
        subdivision,
        lotNum,
        blockNum,
      ]
        .filter(Boolean)
        .join(', ')

      routes2.project
        .create({ data, address })
        .then((res) => {
          if (!RootStore.userStore.projectAdded) {
            RootStore.userStore.fetchUser()
          }
          if (ids) {
            const invoices = ids.split(',')
            if (res.data._id) {
              routes2.project
                .addInvoices(res.data._id, { invoices })
                .then((res2) => {
                  console.log('Result 2', res2, navigation.canGoBack())
                  navigation.goBack()
                  InvoiceActionsStore.resumeApplyingForLoan()
                })
            }
          } else {
            console.log('Result 1', res, navigation.canGoBack())
            navigation.goBack()
            reloadProjects()
          }
        })
        .catch((err) => console.log(err))
    }
  },
})

export const ProjectStoreModel = types.compose(
  'ProjectStore',
  types.model(props).actions(actions).views(views),
  editableModel(),
)
const store = ProjectStoreModel.create({})
export default store
